import { type ReactNode, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Copy, Sparkles, Wand2 } from 'lucide-react';
import type { AiAssistResult, CategoryRef, TagRef } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/toaster';
import { aiErrorMessage, htmlToPlainText, useAiAssist as useAiAssistMutation } from '@/lib/ai';
import { categoriesListQueryOptions, tagsListQueryOptions, useCreateTag } from '@/lib/taxonomies';

export type AiAction =
  | 'rewrite-title'
  | 'title-variants'
  | 'generate-summary'
  | 'proofread'
  | 'suggest-seo'
  | 'suggest-tags'
  | 'suggest-category'
  | 'social-captions';

export const AI_ACTION_LABEL: Record<AiAction, string> = {
  'rewrite-title': 'ปรับหัวข้อ',
  'title-variants': 'หัวข้อหลายแบบ',
  'generate-summary': 'สร้างโปรยข่าว',
  proofread: 'ตรวจคำผิด',
  'suggest-seo': 'แนะนำ SEO',
  'suggest-tags': 'แนะนำแท็ก',
  'suggest-category': 'แนะนำหมวด',
  'social-captions': 'แคปชั่นโซเชียล',
};

export interface AiAssistProps {
  getTitle: () => string;
  getBodyHtml: () => string;
  getSummary: () => string;
  onApplyTitle: (title: string) => void;
  onApplySummary: (summary: string) => void;
  onApplySeo: (v: { metaTitle: string; metaDescription: string; focusKeyword: string }) => void;
  onAddTags: (tags: TagRef[]) => void;
  onAddCategory: (cat: CategoryRef) => void;
}

export interface AiAssistController {
  /** Fire a task. Tags/category apply immediately; the rest open a review dialog. */
  run: (task: AiAction) => void;
  /** True while any task (or its tag/category resolution) is in flight. */
  busy: boolean;
  /** The shared review dialog — render ONCE near the form root. */
  dialog: ReactNode;
}

/**
 * Owns the AI-assist flow so contextual buttons can be scattered across the
 * post form (title / summary / SEO / tags / category) yet share one mutation,
 * one review dialog, and one busy state.
 */
export function useAiAssist(props: AiAssistProps): AiAssistController {
  const [result, setResult] = useState<AiAssistResult | null>(null);
  const [resolving, setResolving] = useState(false);
  const assist = useAiAssistMutation();
  const createTag = useCreateTag();
  const qc = useQueryClient();

  const run = (task: AiAction) => {
    const title = props.getTitle().trim();
    const bodyText = htmlToPlainText(props.getBodyHtml());
    const summary = props.getSummary().trim();

    if (task !== 'proofread' && !title) {
      toast.error('ใส่หัวข้อก่อนใช้ AI');
      return;
    }
    const needsBody =
      task === 'generate-summary' ||
      task === 'suggest-seo' ||
      task === 'suggest-tags' ||
      task === 'suggest-category' ||
      task === 'social-captions';
    if (needsBody && !bodyText) {
      toast.error('ใส่เนื้อข่าวก่อนใช้ AI');
      return;
    }

    void (async () => {
      try {
        let res: AiAssistResult;
        switch (task) {
          case 'rewrite-title':
          case 'title-variants':
            res = await assist.mutateAsync({ task, title, context: bodyText || undefined });
            break;
          case 'generate-summary':
          case 'suggest-tags':
          case 'suggest-category':
          case 'social-captions':
            res = await assist.mutateAsync({ task, title, body: bodyText });
            break;
          case 'suggest-seo':
            res = await assist.mutateAsync({
              task,
              title,
              body: bodyText,
              summary: summary || undefined,
            });
            break;
          case 'proofread':
            res = await assist.mutateAsync({ task, text: bodyText || title });
            break;
        }
        if (res.task === 'suggest-tags') await applyTags(res.tags);
        else if (res.task === 'suggest-category')
          await applyCategory(res.categorySlug, res.categoryName);
        else setResult(res);
      } catch (e) {
        toast.error(aiErrorMessage(e));
      }
    })();
  };

  const applyTags = async (names: string[]) => {
    if (names.length === 0) {
      toast.error('AI ไม่ได้เสนอแท็ก');
      return;
    }
    setResolving(true);
    try {
      const resolved: TagRef[] = [];
      for (const name of names) {
        const list = await qc.fetchQuery(tagsListQueryOptions(name));
        const match = list.items.find((t) => t.name.trim().toLowerCase() === name.toLowerCase());
        if (match) resolved.push({ id: match.id, slug: match.slug, name: match.name });
        else resolved.push(await createTag.mutateAsync({ name }));
      }
      props.onAddTags(resolved);
      toast.success(`เพิ่ม ${resolved.length} แท็กจาก AI`);
    } catch (e) {
      toast.error(aiErrorMessage(e));
    } finally {
      setResolving(false);
    }
  };

  const applyCategory = async (slug: string, name: string) => {
    if (!slug) {
      toast.error('AI ไม่มั่นใจว่าเป็นหมวดใด');
      return;
    }
    const cats = await qc.fetchQuery(categoriesListQueryOptions());
    const match = cats.find((c) => c.slug === slug);
    if (!match) {
      toast.error(`ไม่พบหมวด "${name}"`);
      return;
    }
    props.onAddCategory({ id: match.id, slug: match.slug, name: match.name });
    toast.success(`เพิ่มหมวด "${match.name}"`);
  };

  const dialog = (
    <Dialog open={result != null} onOpenChange={(o) => !o && setResult(null)}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-violet-500" />
            {result ? AI_ACTION_LABEL[result.task] : 'AI ช่วย'}
          </DialogTitle>
          <DialogDescription>
            ผลลัพธ์เป็นเพียงคำแนะนำ — ตรวจทานก่อนใช้เสมอ AI อาจผิดพลาดได้
          </DialogDescription>
        </DialogHeader>
        {result && (
          <ResultView
            result={result}
            onApplyTitle={(t) => {
              props.onApplyTitle(t);
              toast.success('ใช้หัวข้อจาก AI แล้ว');
              setResult(null);
            }}
            onApplySummary={(s) => {
              props.onApplySummary(s);
              toast.success('ใช้โปรยข่าวจาก AI แล้ว');
              setResult(null);
            }}
            onApplySeo={(v) => {
              props.onApplySeo(v);
              toast.success('ใช้ข้อมูล SEO จาก AI แล้ว');
              setResult(null);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );

  return { run, busy: assist.isPending || resolving, dialog };
}

/** Small contextual trigger — place next to the field it acts on. */
export function AiButton({
  action,
  controller,
  label,
}: {
  action: AiAction;
  controller: AiAssistController;
  label?: string;
}) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      disabled={controller.busy}
      onClick={() => controller.run(action)}
      className="h-7 gap-1 px-2 text-xs text-violet-600 hover:text-violet-700 dark:text-violet-400"
      title={AI_ACTION_LABEL[action]}
    >
      <Sparkles className="h-3.5 w-3.5" />
      {label ?? AI_ACTION_LABEL[action]}
    </Button>
  );
}

function ResultView({
  result,
  onApplyTitle,
  onApplySummary,
  onApplySeo,
}: {
  result: AiAssistResult;
  onApplyTitle: (t: string) => void;
  onApplySummary: (s: string) => void;
  onApplySeo: (v: { metaTitle: string; metaDescription: string; focusKeyword: string }) => void;
}) {
  const copy = (text: string) => {
    void navigator.clipboard
      .writeText(text)
      .then(() => toast.success('คัดลอกแล้ว'))
      .catch(() => {});
  };

  return (
    <div className="space-y-3 py-2">
      {result.task === 'rewrite-title' && (
        <ApplyBlock text={result.title} onApply={() => onApplyTitle(result.title)} />
      )}

      {result.task === 'title-variants' && (
        <div className="space-y-2">
          {result.titles.map((t, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onApplyTitle(t)}
              className="hover:border-brand-400 w-full rounded-md border border-slate-200 p-2 text-left text-sm dark:border-slate-700"
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {result.task === 'generate-summary' && (
        <ApplyBlock text={result.summary} onApply={() => onApplySummary(result.summary)} />
      )}

      {result.task === 'suggest-seo' && (
        <div className="space-y-2 text-sm">
          <Field label="Meta title" value={result.metaTitle} />
          <Field label="Meta description" value={result.metaDescription} />
          <Field label="Focus keyword" value={result.focusKeyword} />
          <Button
            type="button"
            size="sm"
            onClick={() =>
              onApplySeo({
                metaTitle: result.metaTitle,
                metaDescription: result.metaDescription,
                focusKeyword: result.focusKeyword,
              })
            }
          >
            ใช้ข้อมูล SEO นี้
          </Button>
        </div>
      )}

      {result.task === 'proofread' && (
        <div className="space-y-2 text-sm">
          {result.changes.length === 0 ? (
            <p className="text-emerald-600 dark:text-emerald-400">ไม่พบคำผิด 👍</p>
          ) : (
            <>
              <p className="text-slate-500">
                พบ {result.changes.length} จุดที่ควรแก้ (แก้เองในเนื้อข่าว):
              </p>
              <ul className="max-h-64 space-y-1.5 overflow-y-auto">
                {result.changes.map((ch, i) => (
                  <li key={i} className="rounded border border-slate-200 p-2 dark:border-slate-700">
                    <span className="text-red-600 line-through dark:text-red-400">{ch.before}</span>{' '}
                    → <span className="text-emerald-600 dark:text-emerald-400">{ch.after}</span>
                    {ch.reason && (
                      <span className="ml-1 text-xs text-slate-400">({ch.reason})</span>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {result.task === 'social-captions' && (
        <div className="space-y-2 text-sm">
          <CaptionRow label="Facebook" value={result.facebook} onCopy={copy} />
          <CaptionRow label="X" value={result.x} onCopy={copy} />
          <CaptionRow label="LINE" value={result.line} onCopy={copy} />
          {result.hashtags.length > 0 && (
            <CaptionRow
              label="Hashtags"
              value={result.hashtags.map((h) => `#${h}`).join(' ')}
              onCopy={copy}
            />
          )}
        </div>
      )}

      <DialogFooter />
    </div>
  );
}

function ApplyBlock({ text, onApply }: { text: string; onApply: () => void }) {
  return (
    <div className="space-y-2">
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-800/50">
        {text}
      </div>
      <Button type="button" size="sm" onClick={onApply}>
        ใช้ข้อความนี้
      </Button>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="rounded border border-slate-200 p-1.5 dark:border-slate-700">
        {value || '—'}
      </div>
    </div>
  );
}

function CaptionRow({
  label,
  value,
  onCopy,
}: {
  label: string;
  value: string;
  onCopy: (t: string) => void;
}) {
  return (
    <div>
      <div className="mb-0.5 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500">{label}</span>
        <button
          type="button"
          onClick={() => onCopy(value)}
          className="hover:text-brand-600 inline-flex items-center gap-1 text-xs text-slate-400"
        >
          <Copy className="h-3 w-3" /> คัดลอก
        </button>
      </div>
      <div className="rounded border border-slate-200 p-1.5 dark:border-slate-700">
        {value || '—'}
      </div>
    </div>
  );
}
